<template>
    <v-dialog :value="value" max-width="640" persistent scrollable>
        <v-card class="ui-card">
            <div class="ui-card__header">
                <div>
                    <h3 class="ui-section-title">{{ court ? "Editar vara" : "Nova vara" }}</h3>
                    <p class="ui-section-description">
                        {{ court ? "Trocar o nome não perde o vínculo: a importação continua reconhecendo o nome antigo." : "Para registrar uma vara antes de aparecer caso nela, ou a lotação de um juiz." }}
                    </p>
                </div>
            </div>
            <div class="ui-card__body">
                <v-text-field v-model="form.name" label="Nome da vara" outlined dense hide-details="auto" autofocus @input="queueSimilar"></v-text-field>
                <similar-list :items="similar" kind="court" />
                <v-row dense class="mt-1">
                    <v-col cols="12" sm="8">
                        <v-autocomplete v-model="form.municipality_id" :items="municipalities" :search-input.sync="search" item-value="id" :item-text="municipalityText"
                            label="Cidade" placeholder="Digite para buscar" outlined dense hide-details="auto" clearable no-filter hide-no-data :menu-props="{ offsetY: true }"
                            @change="pickMunicipality"></v-autocomplete>
                    </v-col>
                    <v-col cols="12" sm="4"><v-select v-model="form.uf" :items="ufs" label="UF" outlined dense hide-details="auto" :menu-props="{ offsetY: true }" @change="queueSimilar"></v-select></v-col>
                    <v-col cols="6" sm="4"><v-text-field v-model="form.tribunal" label="Tribunal" outlined dense hide-details="auto"></v-text-field></v-col>
                    <v-col cols="6" sm="4"><v-text-field v-model="form.system" label="Sistema (ex.: eproc)" outlined dense hide-details="auto"></v-text-field></v-col>
                    <v-col cols="12" sm="4"><v-text-field v-model="form.comarca" label="Comarca" outlined dense hide-details="auto"></v-text-field></v-col>
                </v-row>
                <div class="ui-small ui-muted mt-4 mb-2">Contato do gabinete</div>
                <v-row dense>
                    <v-col cols="12" sm="4"><v-text-field v-model="form.clerk_name" label="Assessor(a)" outlined dense hide-details="auto"></v-text-field></v-col>
                    <v-col cols="12" sm="5"><v-text-field v-model="form.email" label="E-mail" outlined dense hide-details="auto"></v-text-field></v-col>
                    <v-col cols="12" sm="3"><v-text-field v-model="form.phone" label="Telefone" outlined dense hide-details="auto"></v-text-field></v-col>
                    <v-col cols="12"><v-textarea v-model="form.notes" label="Observações" outlined dense rows="2" auto-grow hide-details="auto"></v-textarea></v-col>
                </v-row>
                <v-switch v-if="court" v-model="form.active" inset dense hide-details class="mt-3"
                    :label="form.active ? 'Ativa' : 'Inativa (extinta ou fora de uso): some das listas, mas a importação continua ligando os casos a ela'"></v-switch>
            </div>
            <div class="ui-card__footer">
                <v-btn outlined :disabled="saving" @click="close">Cancelar</v-btn>
                <v-btn v-if="$allowed('save')" color="primary" depressed :loading="saving" :disabled="!form.name.trim() || !form.uf" @click="save">{{ court ? "Salvar" : "Cadastrar" }}</v-btn>
            </div>
        </v-card>
    </v-dialog>
</template>

<script>
import axios from "axios";
import SimilarList from "@/components/prospect/bench/SimilarList.vue";
import { notifyError, notifySuccess } from "@/components/prospect/prospect.util";

const UFS = ["AC", "AL", "AM", "AP", "BA", "CE", "DF", "ES", "GO", "MA", "MG", "MS", "MT", "PA", "PB", "PE", "PI", "PR", "RJ", "RN", "RO", "RR", "RS", "SC", "SE", "SP", "TO"];
const FIELDS = ["name", "uf", "tribunal", "system", "comarca", "clerk_name", "email", "phone", "notes"];

// Cadastro e edicao de vara (Juizes e varas).
export default {
    name: "ProspectCourtFormDialog",

    components: { SimilarList },

    props: {
        value: { type: Boolean, default: false },
        court: { type: Object, default: null }, // null = nova
    },

    data: () => ({ form: { name: "" }, saving: false, similar: [], timer: null, search: null, searchTimer: null, municipalities: [], ufs: UFS }),

    watch: {
        value(open) {
            if (open) this.reset();
        },
        search(term) {
            clearTimeout(this.searchTimer);
            if (!term || term.length < 2) return;
            this.searchTimer = setTimeout(async () => {
                try {
                    const result = await axios.get("/api/admin/prospect/municipalities", { params: { q: term } });
                    const current = this.municipalities.filter((item) => item.id === this.form.municipality_id);
                    this.municipalities = [...current, ...result.data.content.filter((item) => item.id !== this.form.municipality_id)];
                } catch (error) {
                    notifyError(this, error);
                }
            }, 300);
        },
    },

    methods: {
        municipalityText(item) {
            return `${item.name}/${item.uf}`;
        },

        reset() {
            const court = this.court || {};
            this.form = FIELDS.reduce((form, field) => ({ ...form, [field]: court[field] || "" }), { municipality_id: court.municipality_id || null, active: court.active !== false });
            this.form.uf = court.uf || null;
            // A cidade atual aparece no campo sem precisar buscar.
            this.municipalities = court.municipality_id ? [{ id: court.municipality_id, name: court.city || "", uf: court.uf }] : [];
            this.similar = [];
        },

        pickMunicipality(id) {
            const item = this.municipalities.find((entry) => entry.id === id);
            if (item) this.form.uf = item.uf;
            this.queueSimilar();
        },

        queueSimilar() {
            clearTimeout(this.timer);
            this.timer = setTimeout(() => this.loadSimilar(), 350);
        },

        async loadSimilar() {
            if (this.form.name.trim().length < 3) {
                this.similar = [];
                return;
            }
            try {
                const params = { name: this.form.name, uf: this.form.uf || undefined, exclude_id: this.court ? this.court.id : undefined };
                const result = await axios.get("/api/admin/prospect/courts/similar", { params });
                this.similar = result.data.content;
            } catch (error) {
                this.similar = [];
            }
        },

        async save() {
            this.saving = true;
            try {
                const payload = { ...this.form };
                const selected = this.municipalities.find((item) => item.id === payload.municipality_id);
                payload.city = selected ? selected.name : (this.court && !payload.municipality_id ? this.court.city : null);
                if (!this.court) delete payload.active;
                const result = this.court
                    ? await axios.patch(`/api/admin/prospect/courts/${this.court.id}`, payload)
                    : await axios.post("/api/admin/prospect/courts", payload);
                notifySuccess(this, this.court ? "Vara atualizada." : "Vara cadastrada.");
                this.$emit("saved", result.data.content);
                this.close();
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.saving = false;
            }
        },

        close() {
            this.$emit("input", false);
        },
    },
};
</script>
